/* eslint-disable no-new */
import {
  Wrapper,
  InputWrapper,
  useValidator,
  useMapDot,
} from "nystem-components";
import app from "nystem";
import { useEffect, useRef, useState } from "react";
import "./view.css";
import { Point } from "ol/geom.js";
import { Map, View } from "ol/index.js";
import { defaults as defaultControls } from "ol/control/defaults.js";
import { OSM, XYZ } from "ol/source.js";
import { Tile as TileLayer } from "ol/layer.js";
import { useGeographic } from "ol/proj.js";
import "ol/ol.css";
import validate from "./validate";

// eslint-disable-next-line react-hooks/rules-of-hooks
useGeographic();

const MapInput = ({ value, setValue, model, view }) => {
  const mapRef = useRef();
  const layersRef = useRef();
  const [id] = useState(app.uuid);
  const [baseMap, setBaseMap] = useState(
    model.satellite ? "satellite" : "street",
  );
  const [error] = useValidator({ view, validate, value, model });

  useEffect(() => {
    if (value || !model.default !== "devicePos") return;
    app.event("mapsDevicePos").then(({ latitude, longitude }) => {
      setValue({ coordinate: [latitude, longitude], zoom: model.zoom });
    });
  }, [model, setValue, value]);

  useEffect(() => {
    const target = mapRef.current;
    let mapBaseField = false;
    if (model.mapBaseField)
      mapBaseField = app.insertVal(model.mapBaseField, view, path);

    let {
      coordinate = mapBaseField || [13.012, 55.587],
      zoom = mapBaseField?.zoom || 8,
    } = value || {};

    const point = new Point(coordinate);
    const streetLayer = new TileLayer({
      source: new OSM({
        wrapX: false,
      }),
      visible: !model.satellite,
    });
    const satelliteLayer = new TileLayer({
      source: new XYZ({
        url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        wrapX: false,
        crossOrigin: "anonymous",
      }),
      visible: !!model.satellite,
    });
    layersRef.current = { street: streetLayer, satellite: satelliteLayer };

    const map = new Map({
      controls: defaultControls({ zoom: model.zoomControls !== false }),
      layers: [streetLayer, satelliteLayer],
      view: new View({ center: mapBaseField?.coordinate || coordinate, zoom }),
      target,
    });

    map.on("moveend", (e) => {
      zoom = map.getView().getZoom();
      if (model.areaType) coordinate = map.getView().getCenter();

      view.setValue({
        path: model.id,
        value: { coordinate, zoom },
      });
    });

    let feature = false;
    if (!model.areaType) {
      [feature] = useMapDot({
        type: "default",
        points: { coordinate: point, color: "blue", shape: "dot" },
        map,
        tileLayer: streetLayer,
        onClickType: "selected",
        onClick: (pos) => {
          view.event("mapClick", value[pos]);
        },
      });

      map.on("click", (event) => {
        view.setValue({
          path: model.id,
          value: { coordinate: event.coordinate, zoom },
        });
      });
    }

    view.on("change", ({ id, value }) => {
      if (!value[model.id] && id !== model.id) return;

      ({ coordinate = [13.012, 55.587], zoom = 8 } = value[model.id] || {});

      map.getView().setZoom(zoom);

      if (model.areaType) map.getView().setCenter(coordinate);
      else feature.getGeometry().setCoordinates(coordinate);
    });

    return () => {
      layersRef.current = undefined;
      map.setTarget(null);
      target.innerHTML = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectBaseMap = (baseMap) => {
    Object.entries(layersRef.current || {}).forEach(([id, layer]) => {
      layer.setVisible(id === baseMap);
    });
    setBaseMap(baseMap);
  };

  return (
    <InputWrapper id={id} model={model} error={error}>
      <Wrapper className="relative overflow-hidden rounded-lg border border-stone-300">
        <Wrapper ref={mapRef} className={model.classNameInput}></Wrapper>
        <Wrapper className="absolute right-3 top-3 z-10 flex overflow-hidden rounded-lg border border-stone-300 bg-white/95 shadow print:hidden">
          {[
            ["street", "Street"],
            ["satellite", "Satellite"],
          ].map(([id, text]) => (
            <button
              key={id}
              type="button"
              className={`px-3 py-2 text-xs font-semibold ${
                baseMap === id
                  ? "bg-emerald-800 text-white shadow-inner"
                  : "bg-white text-stone-700 hover:bg-stone-100"
              }`}
              aria-pressed={baseMap === id}
              onClick={() => selectBaseMap(id)}
            >
              {text}
            </button>
          ))}
        </Wrapper>
      </Wrapper>
    </InputWrapper>
  );
};

export default MapInput;
